import { Feature, FeatureCollection } from "geojson";
import { anyType } from "../types";
import { fiwareDataItemType, fiwareDataType, fiwareType } from "../types/lib/fiware";

export function* walkCategoriesFiware(data: {[key: string]: fiwareDataType }): Generator<fiwareDataItemType, void, unknown> {
    for (const x in data) {
        if ('items' in data[x]) {
            yield* walkCategoriesFiware(data[x].items);
        } else {
            yield data[x];
        }
    }
};


export type legendType = {
    [key:string]: {
        [key:string]: string;
    }
};

export type formatedFiwareDataType = {
    [key:string]: string[] | FeatureCollection
};

const FIXED_UNDERPASS_INFORMATION_RESPONSE = {
    error: false,
    data: [{
        id: 'UnderpassInformation',
        msg: {data: ['以下のサイトをご確認ください', '[国土交通省浸水センサ表示システム](https://c-sensor.river.go.jp/map/index?x=15394508.07996&y=4144291.31202&z=12.33115&r=0&l=101111)']}
    }]
}

/* **************
 * fiwareデータの取得
 * **************/
export const firstFetchFiwareData = async (urls: {[key:string]: string} | undefined) 
    : Promise<{ fiwareData: formatedFiwareDataType | undefined; legendData: legendType | undefined; }> => {

    if(!urls) { return { fiwareData: undefined, legendData: undefined }; }

    const newFiwareData: formatedFiwareDataType = {};
    const legendData: legendType = {};

    try {
        const fetchTasks = Object.values(urls).map(url => {
            if (url.includes('/yaizu-UnderpassInformation/')) {
                return Promise.resolve(FIXED_UNDERPASS_INFORMATION_RESPONSE)
            }
            return fetch(url).then(res => res.json()).catch(e => {
                console.error('Failed to fetch fiware data: ', e);
                return null;
            })
        });
        const responses = await Promise.all(fetchTasks);
        for (const json of responses) {
            try {
                if (!json?.data?.[0]?.msg) {
                    console.warn('Invalid data structure in response');
                    continue;
                }
                const data = json.data[0].msg.data;
                const id = json.data[0].id;
                legendData[id] = json.data[0].msg.legend;
                
                if (!data || Object.keys(data).length === 0) { continue; }
                const formatedData = formatFiwareData(data);
                newFiwareData[id] = formatedData;

            } catch (e) {
                console.error('Error processing response: ', e);
            }
        }
    } catch (e) {
        console.error('Failed to fetch fiware data: ', e);
    }
    
    return { fiwareData: newFiwareData, legendData: legendData };
};


const formatFiwareData = (data: fiwareType[] | string | string[] | FeatureCollection): string[] | FeatureCollection => {
    // stringの配列の場合、配列で返す
    if(typeof data === 'string') {
        return [data];
    }

    // string配列か、featureCollectionの場合、そのまま返す
    if(
        (Array.isArray(data) && 
        data.every(item => typeof item === 'string')) || 
        'type' in (data as unknown as FeatureCollection)
    ) {
        return data as string[] | FeatureCollection;
    }

    // それ以外は、
    const features: Feature[] = (data as fiwareType[]).map((item: fiwareType) => {
        return {
            type: 'Feature',
            geometry: item.Location,
            properties: item
        };
    });

    return { type: 'FeatureCollection', features: features };
};



/* **************
 * geojsonに変換
 * **************/
export const formatToJSON = (data: anyType[]) => {
    // stringの配列の場合、アラート表示にする
    if(Array.isArray(data) && data.every(item => typeof item === 'string')) {
        return data;
    } 

    const features = data.map((item: anyType) => {
        return {
            type: 'Feature',
            geometry: item.Location,
            properties: item
        };
    });

    return { type: 'FeatureCollection', features: features };
};


/* **************
 * fiwareデータを更新
 * **************/
export const updateFiwareData = (updatedDataId: string, oldJson: {[key: string]: anyType;} | undefined, newJson: {[key: string]: anyType[];}) => {

    const newFiwareData = oldJson ? { ...oldJson } : {};       // 旧データを取得
    let isUpdated = false;

    // oldJsonから更新するデータを取得
    const target = newFiwareData && updatedDataId in newFiwareData ? newFiwareData[updatedDataId] : undefined;

    // 更新するデータがない場合、または更新するデータがUnderpassInformationの場合は、jsonを変換せずにそのまま返す
    if(!target || updatedDataId === 'UnderpassInformation') {
        return { newFiwareData, isUpdated }; 
    }

    // jsonをgeojsonまたはアラート表示形式に変換
    const data = Array.isArray(newJson) ? formatToJSON(newJson) : newJson;

    // データが存在する場合、更新
    if (data && JSON.stringify(target) !== JSON.stringify(data)) {
        newFiwareData[updatedDataId] = data;
        isUpdated = true;
    }
    
    return { newFiwareData, isUpdated };
}
